import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';

import '../DataExtraction/style.css';
import callApi from '../../api/callApi';

const MAX_FILES = 12;
const API_URL = process.env.REACT_APP_CREDIT_PROFILE_API_URL;
const PROFILE_TYPES = [
    { value: 'sme', label: 'SME' },
    { value: 'sole_trader', label: 'Sole Trader' },
    { value: 'consumer', label: 'Consumer' },
];
const PROFILE_TYPE_LABELS = Object.fromEntries(PROFILE_TYPES.map(t => [t.value, t.label]));

const BuildProfile = () => {
    const navigate = useNavigate();
    const [enabledAPIs, setEnabledAPIs] = useState(null); // null = loading
    const [providers, setProviders] = useState([]); // allowed provider_id values from the API
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [result, setResult] = useState(null); // API response: {profile_id, status, message, files, applicants}
    const [error, setError] = useState(null); // request-level error message (invalid request, network, ...)
    const fileInputRef = useRef(null);

    useEffect(() => {
        const url = `${process.env.REACT_APP_API_URL}/v1/portal/enabled_apis`;
        callApi(url, 'GET')
            .then((json) => setEnabledAPIs((json && json.data && json.data.enabled_apis) || []))
            .catch((err) => {
                console.error('Error fetching enabled APIs:', err);
                setEnabledAPIs([]);
            });
    }, []);

    useEffect(() => {
        if (!API_URL || !enabledAPIs || !enabledAPIs.includes('credit_profile')) return;
        callApi(`${API_URL}/providers`, 'GET')
            .then((json) => setProviders((json && json.providers) || []))
            .catch((err) => {
                console.error('Error fetching providers:', err);
                setProviders([]);
            });
    }, [enabledAPIs]);

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
                resolve(fileReader.result);
            };
            fileReader.onerror = (error) => {
                reject(error);
            };
        });
    };

    const handleMultipleFiles = async (e, setFieldValue, setFieldError) => {
        const files = Array.from(e.target.files);

        if (files.length === 0) {
            return;
        }

        setFieldError('files', '');

        try {
            const processedFiles = [];
            for (const file of files) {
                const base64 = await convertToBase64(file);
                processedFiles.push({
                    file_name: file.name,
                    pdf_data: base64.split(',')[1],
                    password: ''
                });
            }

            setUploadedFiles(processedFiles);
            setFieldValue('files', processedFiles);
            setResult(null);
            setError(null);
        } catch (error) {
            console.error('Error converting files to base64:', error);
            setFieldError('files', 'Error converting PDF files to base64!');
            setUploadedFiles([]);
            setFieldValue('files', []);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const updateFilePassword = (index, password, setFieldValue, values) => {
        const updatedFiles = [...values.files];
        updatedFiles[index].password = password;
        setFieldValue('files', updatedFiles);
        setUploadedFiles(updatedFiles);
    };

    const removeFile = (index, setFieldValue, values) => {
        const updatedFiles = values.files.filter((_, i) => i !== index);
        setFieldValue('files', updatedFiles);
        setUploadedFiles(updatedFiles);
        if (updatedFiles.length === 0 && fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = (values, setSubmitting) => {
        setResult(null);
        setError(null);

        // Synchronous API: extraction + enrichment + credit profile (~40-90 s). Inputs and
        // outputs are stored server-side; the response is JSON:
        //   200 {status: 'completed', ...} / 422 {status: 'failed', ...} -> result card
        //   400/500/503 {status: 'error', message} -> error card
        // callApi logs out on 401 and alerts (returning the Response) on 403/404.
        const body = { files: values.files, profile_type: values.profile_type };
        if (values.provider_id) {
            body.provider_id = values.provider_id; // optional; stored, not used yet
        }
        callApi(`${API_URL}/credit_profile`, 'POST', body)
            .then((res) => {
                if (res && (res.status === 'completed' || res.status === 'failed')) {
                    setResult(res);
                }
                else if (res instanceof Response) {
                    setError(`Request failed (HTTP ${res.status})`);
                }
                else {
                    setError((res && res.message) || 'Failed to build the credit profile. Please try again.');
                }
                setSubmitting(false);
            })
            .catch((err) => {
                console.error('Error:', err);
                setError('Failed to build the credit profile. Please try again.');
                setSubmitting(false);
            });
    };

    const failedFiles = result ? (result.files || []).filter(f => f.status === 'failed') : [];

    return (
        <div className="index-page">
            <div className="container-fluid">
                {/* Header */}
                <div className="row justify-content-center">
                    <div className="col-lg-10 col-xl-8">
                        <div className="text-center mb-5">
                            <h1 className="title mb-3">Build Credit Profile</h1>
                        </div>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-lg-10 col-xl-8">
                        <div className="card shadow-sm mb-4">
                            <div className="card-body p-4">
                                {enabledAPIs === null ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-secondary mb-3" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div className="text-muted">Loading configuration...</div>
                                    </div>
                                ) : !enabledAPIs.includes('credit_profile') ? (
                                    <div className="alert alert-warning mb-0" role="alert">
                                        Credit Profile is not enabled for your account. Please contact Dobin.
                                    </div>
                                ) : (
                                <>
                                {!API_URL && (
                                    <div className="alert alert-warning" role="alert">
                                        Credit Profile API URL is not configured (REACT_APP_CREDIT_PROFILE_API_URL).
                                    </div>
                                )}
                                <Formik
                                    initialValues={{ files: [], profile_type: 'sme', provider_id: '' }}
                                    validate={values => {
                                        const errors = {};
                                        if (!values.files || values.files.length === 0) {
                                            errors.files = 'At least one PDF file is required';
                                        }
                                        else if (values.files.length > MAX_FILES) {
                                            errors.files = `Maximum ${MAX_FILES} files allowed per request`;
                                        }
                                        else {
                                            const invalidFiles = values.files.filter(file => !file.file_name?.toLowerCase().endsWith('.pdf'));
                                            if (invalidFiles.length > 0) {
                                                errors.files = `Please select only PDF files! Invalid files: ${invalidFiles.map(f => f.file_name).join(', ')}`;
                                            }
                                        }
                                        return errors;
                                    }}
                                    onSubmit={(values, { setSubmitting }) => {
                                        handleSubmit(values, setSubmitting);
                                        setSubmitting(true);
                                    }}
                                >
                                    {({ values, isSubmitting, setFieldValue }) => (
                                        <Form>
                                            {/* PDF Upload */}
                                            <div className="row mb-4">
                                                <div className="col-md-3 d-flex align-items-start">
                                                    <label className="form-label fw-semibold text-start text-nowrap">
                                                        PDF Documents <span className="text-danger">*</span>
                                                    </label>
                                                </div>
                                                <div className="col-md-9">
                                                    <Field name='files'>
                                                        {({ form }) => (
                                                            <div>
                                                                <input
                                                                    ref={fileInputRef}
                                                                    type="file"
                                                                    accept=".pdf"
                                                                    multiple={true}
                                                                    className="form-control"
                                                                    disabled={isSubmitting}
                                                                    onChange={(e) => handleMultipleFiles(e, form.setFieldValue, form.setFieldError)}
                                                                />
                                                                <div className="mt-2 text-start">
                                                                    <small className="text-muted">
                                                                        Select up to {MAX_FILES} PDF files. Malaysian bank statements, digital PDFs only (scanned PDFs are not supported).
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Field>
                                                    <ErrorMessage
                                                        className="text-danger small mt-1"
                                                        name="files"
                                                        component="div"
                                                    />
                                                </div>
                                            </div>

                                            {/* Uploaded Files List */}
                                            {uploadedFiles.length > 0 && (
                                                <div className="row mb-4">
                                                    <div className="col-md-3 d-flex align-items-start">
                                                        <label className="form-label fw-semibold">Uploaded Files</label>
                                                    </div>
                                                    <div className="col-md-9">
                                                        <div className="border rounded p-3 bg-light">
                                                            {uploadedFiles.map((file, index) => (
                                                                <div key={index} className="d-flex align-items-center justify-content-between mb-2 p-2 bg-white rounded border">
                                                                    <div className="flex-grow-1 me-3">
                                                                        <div className="d-flex align-items-center mb-2">
                                                                            <i className="fas fa-file-pdf text-danger me-2"></i>
                                                                            <span className="fw-medium">{file.file_name}</span>
                                                                        </div>
                                                                        <div className="row">
                                                                            <div className="col-md-12">
                                                                                <input
                                                                                    type="password"
                                                                                    className="form-control form-control-sm"
                                                                                    placeholder="Leave blank if PDF is not password protected"
                                                                                    value={file.password}
                                                                                    disabled={isSubmitting}
                                                                                    onChange={(e) => updateFilePassword(index, e.target.value, setFieldValue, values)}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {!isSubmitting && (
                                                                        <div
                                                                            className="text-danger cursor-pointer p-1"
                                                                            style={{ cursor: 'pointer', fontSize: '18px' }}
                                                                            onClick={() => removeFile(index, setFieldValue, values)}
                                                                            title="Remove file"
                                                                        >
                                                                            ×
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            <div className="text-center mt-2">
                                                                <small className="text-success">
                                                                    <i className="fas fa-check-circle me-1"></i>
                                                                    {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} uploaded successfully
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Options */}
                                            <div className="border-top pt-4">
                                                <h6 className="mb-3 text-muted">Processing Options</h6>
                                                <div className="row mb-3">
                                                    <div className="col-md-3 d-flex align-items-center">
                                                        <label className="form-label fw-semibold text-start mb-0" htmlFor="profile_type">Profile Type</label>
                                                    </div>
                                                    <div className="col-md-9">
                                                        <Field as="select" id="profile_type" name="profile_type" className="form-select" disabled={isSubmitting}>
                                                            {PROFILE_TYPES.map(t => (
                                                                <option key={t.value} value={t.value}>{t.label}</option>
                                                            ))}
                                                        </Field>
                                                        <div className="mt-1 text-start">
                                                            <small className="text-muted">SME and Sole Trader use the business profile; Consumer uses the consumer profile.</small>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row mb-4">
                                                    <div className="col-md-3 d-flex align-items-center">
                                                        <label className="form-label fw-semibold text-start mb-0" htmlFor="provider_id">Provider</label>
                                                    </div>
                                                    <div className="col-md-9">
                                                        <Field as="select" id="provider_id" name="provider_id" className="form-select" disabled={isSubmitting}>
                                                            <option value="">None</option>
                                                            {providers.map(p => (
                                                                <option key={p} value={p}>{p}</option>
                                                            ))}
                                                        </Field>
                                                        <div className="mt-1 text-start">
                                                            <small className="text-muted">Optional. The bank the profile is prepared for, e.g. gxb_my for GX Bank, Malaysia.</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Submit & Clear Buttons */}
                                            <div className="text-center pt-3">
                                                <div className="d-flex justify-content-center gap-3">
                                                    <button
                                                        className="btn btn-primary btn-lg px-5"
                                                        type="submit"
                                                        disabled={
                                                            !API_URL || isSubmitting || !values.files || values.files.length === 0 ||
                                                            result !== null || error !== null
                                                        }
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm text-secondary me-2"></span>
                                                                Building...
                                                            </>
                                                        ) : (
                                                            'Build Profile'
                                                        )}
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-lg px-5"
                                                        type="button"
                                                        disabled={
                                                            isSubmitting ||
                                                            (result === null && error === null && uploadedFiles.length === 0)
                                                        }
                                                        onClick={() => {
                                                            if (fileInputRef.current) {
                                                                fileInputRef.current.value = '';
                                                            }
                                                            setFieldValue('files', []);
                                                            setUploadedFiles([]);
                                                            setResult(null);
                                                            setError(null);
                                                        }}
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                                {isSubmitting && (
                                                    <div className="mt-3">
                                                        <small className="text-muted">
                                                            Extracting, enriching and profiling the statements. This usually takes about 1 minute per applicant; please keep this page open.
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                                </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Result: completed */}
                {result && result.status === 'completed' && (
                    <div className="row justify-content-center">
                        <div className="col-lg-10 col-xl-8">
                            <div className="card border-success shadow-sm mb-4">
                                <div className="card-body text-center">
                                    <i className="fas fa-check-circle text-success mb-3" style={{ fontSize: '3rem' }}></i>
                                    <h4 className="text-success mb-3">Credit Profile Ready</h4>
                                    {(result.applicants || []).map((a, i) => (
                                        <p className="mb-1" key={i}>
                                            <strong>{a.account_holder_name || 'Applicant'}</strong>: built as {PROFILE_TYPE_LABELS[result.profile_type] || result.profile_type}
                                            {a.detected_profile_type ? ` (classifier detected ${a.detected_profile_type})` : ''}
                                        </p>
                                    ))}
                                    {result.provider_id && (
                                        <p className="mb-1 text-muted">Provider: {result.provider_id}</p>
                                    )}
                                    <p className="mt-3 mb-1">
                                        {(result.files || []).length - failedFiles.length} of {(result.files || []).length} file{(result.files || []).length > 1 ? 's' : ''} processed successfully.
                                    </p>
                                    {failedFiles.length > 0 && (
                                        <ul className="mb-2 text-start d-inline-block text-danger">
                                            {failedFiles.map((f, i) => (
                                                <li key={i}><strong>{f.file_name}</strong>: {f.error}</li>
                                            ))}
                                        </ul>
                                    )}
                                    <div className="mt-3">
                                        <button className="btn btn-success" type="button" onClick={() => navigate('/view-profiles')}>
                                            <i className="fas fa-folder-open me-2"></i>Go to View Profiles to download
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Result: failed (processing ran but no profile) or request-level error */}
                {((result && result.status === 'failed') || error) && (
                    <div className="row justify-content-center">
                        <div className="col-lg-10 col-xl-8">
                            <div className="card border-danger shadow-sm mb-4">
                                <div className="card-body">
                                    <div className="alert alert-danger mb-0" role="alert">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        <strong>Failed:</strong> {result ? result.message : error}
                                        {failedFiles.length > 0 && (
                                            <ul className="mb-0 mt-2 text-start">
                                                {failedFiles.map((f, i) => (
                                                    <li key={i}><strong>{f.file_name}</strong>: {f.error}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BuildProfile;
